import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Select from 'react-select';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import Loader from '../Loader';

const ExportRegistration = () => {
    const [name,   setName]   = useState('');
    const [token,  setToken]  = useState('');
    const [expire, setExpire] = useState('');
    const [role,   setRole]   = useState('');
    const [access, setAccess] = useState([]);
    const [user,   setUser]   = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [msg, setMsg] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate  = useNavigate();
    const location  = useLocation();

    const [companies,   setCompanies]   = useState([]);
    const [selectedCid, setSelectedCid] = useState(location.state?.cid || '');
    const [batches,     setBatches]     = useState([]);
    const [selectedBatch, setSelectedBatch] = useState('');
    const [exporting,   setExporting]   = useState(false);
    const [exportResult, setExportResult] = useState(null);

    useEffect(() => { refreshToken(); getUserAccess(); }, []);
    useEffect(() => { if (role > 2) navigate('/dashboard'); }, [role]);
    useEffect(() => {
        if (user !== null && role !== '' && parseInt(role) > 1 && !user.hasBase) navigate('/dashboard');
    }, [user, role]);
    useEffect(() => { if (token && user !== null) loadCompanies(); }, [token, user]);
    useEffect(() => { if (token && user !== null && selectedCid) { loadBatches(); setSelectedBatch(''); } }, [token, user, selectedCid]);

    const refreshToken = async () => {
        try {
            const res = await axios.get(`${baseUrl}/token`);
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setRole(decoded.role); setExpire(decoded.exp);
        } catch { navigate('/'); window.location.reload(); }
    };
    const getUserAccess = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/user-access`, { headers: { Authorization: `Bearer ${token}` } });
            setAccess(res.data.results);
            setUser({ hasBase: !!res.data.enrms_registration_switch });
        } catch (e) { navigate('/dashboard'); }
    };

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        if (expire * 1000 < Date.now()) {
            const res = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${res.data.accessToken}`;
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setExpire(decoded.exp);
        }
        return config;
    }, err => Promise.reject(err));

    const loadCompanies = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/setup/company-list`, { headers: { Authorization: `Bearer ${token}` } });
            setCompanies(res.data.data);
        } catch (e) { console.log(e); }
    };

    const loadBatches = async () => {
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/setup/export/batch-list?cid=${selectedCid}`, { headers: { Authorization: `Bearer ${token}` } });
            setBatches(res.data.data);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };

    // Trigger download from base64 content
    const downloadFile = (filename, base64Content, mimeType) => {
        const byteChars = atob(base64Content);
        const byteArr   = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: mimeType });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleExport = async () => {
        if (!selectedCid || !selectedBatch) {
            setIsSuccess(false);
            setMsg('Please select both a customer and a batch.');
            return;
        }
        setExporting(true);
        setExportResult(null);
        setMsg('');
        try {
            const res = await axiosJWT.post(`${baseUrl}/registration/setup/export`,
                { cid: selectedCid, batch_id: selectedBatch },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = res.data;
            setExportResult(data);
            setIsSuccess(true);
            setMsg(`Export ready. 1 XLSX + ${data.csvFiles.length} CSV file(s) generated.`);
        } catch (e) {
            setIsSuccess(false);
            setMsg(e.response?.data?.msg || 'Export failed.');
        }
        setExporting(false);
    };

    const handleDownloadAll = () => {
        if (!exportResult) return;
        const xlsx = exportResult.xlsx;
        downloadFile(xlsx.filename, xlsx.content, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        exportResult.csvFiles.forEach(f => {
            setTimeout(() => downloadFile(f.filename, f.content, 'text/csv'), 300);
        });
    };

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {(user === null || popupOpen) && <Loader />}
                <div className="content-page admin-attendance-page">
                    <div className="row">
                        <div className="col-6"><div className="left-title"><h1>Export Registration Data</h1></div></div>
                        <div className="col-6 inventory-btn">
                            <div className="addnew-button"><Link to="/enrms-registration-setup" state={{ cid: selectedCid }}><span>&larr;</span> Back</Link></div>
                        </div>
                    </div>

                    <p className={`has-text-centered ${isSuccess ? 'success' : 'error'}`}>{msg}</p>

                    <div className="row">
                        <div className="col-12 common-white-shadow-background setting-section">
                            {/* Step 1: Customer */}
                            <div className="row" style={{ marginBottom:'15px' }}>
                                <div className="col-md-4">
                                    <label><strong>Step 1:</strong> Select Customer</label>
                                    <select className="form-control" value={selectedCid} onChange={e => { setSelectedCid(e.target.value); setSelectedBatch(''); }}>
                                        <option value="">— Select Customer —</option>
                                        {companies.map(c => <option key={c.id} value={c.id}>{c.cname}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Step 2: Batch */}
                            {selectedCid && (
                                <div className="row" style={{ marginBottom:'15px' }}>
                                    <div className="col-md-6">
                                        <label><strong>Step 2:</strong> Select E-NRMS Batch (most recent first)</label>
                                        {batches.length === 0
                                            ? <p><em>No published batches found for this customer.</em></p>
                                            : <Select
                                                options={batches.map(b => ({
                                                    value: String(b.id),
                                                    label: `Batch ${b.id} — ${b.date ? new Date(b.date).toLocaleDateString() : b.created_at?.split('T')[0]}`
                                                }))}
                                                value={selectedBatch ? { value: selectedBatch, label: (() => { const b = batches.find(x => String(x.id) === selectedBatch); return b ? `Batch ${b.id} — ${b.date ? new Date(b.date).toLocaleDateString() : b.created_at?.split('T')[0]}` : ''; })() } : null}
                                                onChange={opt => setSelectedBatch(opt ? opt.value : '')}
                                                placeholder="Search or select a batch..."
                                                isClearable
                                                styles={{ container: base => ({ ...base, marginTop: '8px' }) }}
                                            />
                                        }
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Export */}
                            {selectedCid && selectedBatch && (
                                <div className="row" style={{ marginBottom:'15px' }}>
                                    <div className="col-12">
                                        <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
                                            {exporting ? 'Generating...' : 'Export'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Download Results */}
                            {exportResult && (
                                <div style={{ marginTop:'20px', padding:'15px', background:'#f0f9f0', borderRadius:'6px' }}>
                                    <h5>Export Ready</h5>
                                    <div style={{ marginBottom:'10px' }}>
                                        <strong>XLSX:</strong> {exportResult.xlsx.filename}
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            style={{ marginLeft:'10px' }}
                                            onClick={() => downloadFile(exportResult.xlsx.filename, exportResult.xlsx.content, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}
                                        >
                                            Download XLSX
                                        </button>
                                    </div>
                                    {exportResult.csvFiles.length > 0 && (
                                        <div>
                                            <strong>CSV Files ({exportResult.csvFiles.length}):</strong>
                                            <div style={{ maxHeight:'200px', overflowY:'auto', marginTop:'5px' }}>
                                                {exportResult.csvFiles.map((f, i) => (
                                                    <div key={i} style={{ marginBottom:'5px' }}>
                                                        {f.filename}
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            style={{ marginLeft:'10px' }}
                                                            onClick={() => downloadFile(f.filename, f.content, 'text/csv')}
                                                        >
                                                            Download
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <button className="btn btn-primary" style={{ marginTop:'10px' }} onClick={handleDownloadAll}>
                                        Download All Files
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportRegistration;
